import React from 'react';
import { motion } from 'motion/react';
import {
  Cpu,
  Droplets,
  Thermometer,
  Leaf,
  CloudRain,
  Smartphone,
  MapPin,
  Bell,
  Calendar,
  Settings,
  Shield,
  Wrench,
  Mail,
  Phone,
  Brain,
  BarChart3,
  AlertTriangle,
  Sprout,
  TrendingUp,
  Clock,
  Zap,
  Target,
  CheckCircle,
  ArrowRight,
  Check,
  Globe,
  Building2,
  Users,
  Facebook,
  Linkedin,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PDFDownloadButton from '../components/PDFDownloadButton';
import '../styles/techiqbox.css';

const measurements = [
  { icon: Droplets, title: 'Soil Moisture', desc: 'Tracks water availability in the soil', color: 'blue' },
  { icon: Leaf, title: 'Soil pH Levels', desc: 'Ensures the soil supports proper nutrient uptake', color: 'green' },
  { icon: Sprout, title: 'Soil Nutrient Content', desc: 'Measures essential soil nutrition needed for crop health', color: 'emerald' },
  { icon: Thermometer, title: 'Soil Temperature', desc: 'Helps predict crop performance and growth conditions', color: 'orange' },
  { icon: CloudRain, title: 'Farm Humidity', desc: 'Supports better planning and risk management', color: 'cyan' },
  { icon: Settings, title: 'Additional Parameters', desc: 'Supported depending on the setup and deployment needs', color: 'purple' },
];

const coreFeatures = [
  { icon: Cpu, title: 'Mobile Sensor Platform', desc: 'Easy deployment across farms' },
  { icon: Smartphone, title: 'Mobile App Access', desc: 'View readings anytime, anywhere' },
  { icon: MapPin, title: 'Location Tracking', desc: 'Support mapping, planning, and farm management' },
  { icon: Calendar, title: 'Remote Scheduling', desc: 'Adjust schedules and farm outputs through the app' },
  { icon: Bell, title: 'Warnings & Risk Alerts', desc: 'Instant notification of potential issues' },
  { icon: Settings, title: 'Automatic Calibration', desc: 'Reduce manual effort and improve accuracy' },
  { icon: Shield, title: 'Industrial-Grade Sensors', desc: 'Durability and precision built-in' },
  { icon: Wrench, title: 'Easy DIY Installation', desc: 'Clear instructions for quick setup' },
  { icon: Mail, title: 'Email & Phone Notifications', desc: 'Alerts delivered directly to the farmer' },
];

const aiFeatures = [
  { icon: BarChart3, title: 'Real-Time Analysis', desc: 'Analyze farm conditions as they happen' },
  { icon: Target, title: 'Pattern Detection', desc: 'Detect patterns and anomalies in your data' },
  { icon: AlertTriangle, title: 'Predictive Alerts', desc: 'Predict potential problems before they happen' },
  { icon: Droplets, title: 'Smart Irrigation', desc: 'Support smarter irrigation and water management' },
  { icon: Leaf, title: 'Crop Disease Detection', desc: 'AI-based detection through machine learning' },
];

const farmerBenefits = [
  'Improve soil health management',
  'Reduce water waste through moisture tracking',
  'Track nutrient status and prevent poor yields',
  'Improve planting and fertilization timing',
  'Detect risks early (dryness, nutrient deficiency, poor pH)',
];

const institutionBenefits = [
  'Real-time farm monitoring across regions',
  'Soil intelligence and large-scale farm oversight',
  'Data export for research and reporting',
  'Faster response to field-level issues',
  'Long-term tracking of trends and performance',
];

const whyChoose = [
  { icon: Leaf, title: 'Sustainable Farming', desc: 'Reduced wastage through smart monitoring' },
  { icon: TrendingUp, title: 'Cost Savings', desc: 'Optimized resource use and efficiency' },
  { icon: Zap, title: 'Better Productivity', desc: 'Automation and continuous monitoring' },
  { icon: Globe, title: 'Remote Management', desc: 'Real-time insight from anywhere' },
];

const TechIQBoxPage = () => {
  return (
    <div className="techiq-page" id="techiqbox-content">
      <Header />
      
      {/* Hero Section */}
      <section className="techiq-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="hero-content-techiq"
          >
            <span className="hero-badge-techiq">
              <Cpu size={16} /> IoT Smart Agriculture
            </span>
            <h1>TechIQ Smart Farming Box</h1>
            <p className="hero-subtitle-techiq">Your Farm's Real-Time Intelligence Hub</p>
            <p className="hero-desc-techiq">
              A smart IoT sensor platform designed to help farmers and agricultural institutions 
              monitor, understand, and optimize farming operations through real-time data, 
              AI analysis, and automated alerts.
            </p>
            <div className="hero-buttons-techiq">
              <a href="#features" className="btn-techiq-primary">
                Explore Features <ArrowRight size={18} />
              </a>
              <a href="#contact" className="btn-techiq-secondary">
                Request Demo
              </a>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hero-visual-techiq"
          >
            <div className="sensor-box-visual">
              <div className="sensor-icon-large">
                <Cpu size={80} />
              </div>
              <div className="sensor-readings">
                <div className="reading"><Droplets size={16} /> 45% Moisture</div>
                <div className="reading"><Thermometer size={16} /> 24°C Soil Temp</div>
                <div className="reading"><Leaf size={16} /> pH 6.5</div>
              </div>
            </div>
          </motion.div>
        </div>
        
        <div className="hero-stats-techiq">
          <div className="stat">
            <span className="stat-value">6+</span>
            <span className="stat-label">Sensor Types</span>
          </div>
          <div className="stat">
            <span className="stat-value">24/7</span>
            <span className="stat-label">Monitoring</span>
          </div>
          <div className="stat">
            <span className="stat-value">AI</span>
            <span className="stat-label">Powered Analysis</span>
          </div>
          <div className="stat">
            <span className="stat-value">Real-Time</span>
            <span className="stat-label">Alerts</span>
          </div>
        </div>
      </section>

      {/* Measurements Section */}
      <section className="techiq-measurements" id="measurements">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-header-techiq"
          >
            <span className="section-tag-techiq">Key Measurements</span>
            <h2>What the Smart Farming Box Measures</h2>
            <p>Critical environmental and soil-health readings to help farmers make faster and more accurate decisions</p>
          </motion.div>
          
          <div className="measurements-grid">
            {measurements.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`measurement-card ${item.color}`}
              >
                <div className="measurement-icon">
                  <item.icon size={28} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="techiq-features" id="features">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-header-techiq"
          >
            <span className="section-tag-techiq">Smart Features</span>
            <h2>Built for Modern Farming</h2>
            <p>The TechIQ Smart Farming Box is more than a sensor—it's a complete smart farming assistant</p>
          </motion.div>
          
          <div className="features-grid-techiq">
            {coreFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="feature-card-techiq"
              >
                <div className="feature-icon-techiq">
                  <feature.icon size={24} />
                </div>
                <div className="feature-content-techiq">
                  <h4>{feature.title}</h4>
                  <p>{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Intelligence Section */}
      <section className="techiq-ai">
        <div className="container-custom">
          <div className="ai-content-wrapper">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="ai-text-content"
            >
              <span className="section-tag-techiq">AI-Powered Intelligence</span>
              <h2>Smart Analysis for Smarter Farming</h2>
              <p className="ai-intro">
                The Smart Farming Box is supported by TechIQ's AI system, designed to transform 
                raw sensor data into actionable insights.
              </p>
              
              <div className="ai-features-list">
                {aiFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="ai-feature-item"
                  >
                    <div className="ai-feature-icon">
                      <feature.icon size={20} />
                    </div>
                    <div>
                      <h4>{feature.title}</h4>
                      <p>{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="ai-visual"
            >
              <div className="ai-brain-visual">
                <Brain size={100} className="brain-icon" />
                <div className="ai-pulse"></div>
                <div className="ai-pulse delay-1"></div>
                <div className="ai-pulse delay-2"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="techiq-usecases">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-header-techiq"
          >
            <span className="section-tag-techiq">Use Cases</span>
            <h2>What the Box Is Best For</h2>
          </motion.div>
          
          <div className="usecases-grid">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="usecase-card farmers"
            >
              <div className="usecase-icon">
                <Sprout size={32} />
              </div>
              <h3>Ideal for Crop Farmers</h3>
              <p>The Smart Farming Box helps farmers optimize their operations:</p>
              <ul>
                {farmerBenefits.map((benefit, index) => (
                  <li key={index}>
                    <Check size={16} /> {benefit}
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="usecase-card institutions"
            >
              <div className="usecase-icon">
                <Building2 size={32} />
              </div>
              <h3>Agricultural Programs & Institutions</h3>
              <p>Strong value for large-scale agricultural oversight:</p>
              <ul>
                {institutionBenefits.map((benefit, index) => (
                  <li key={index}>
                    <Check size={16} /> {benefit}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="techiq-why">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-header-techiq light"
          >
            <span className="section-tag-techiq">Why TechIQ</span>
            <h2>Why Farmers Choose TechIQ</h2>
            <p>TechIQ Smart Farming Box supports better, smarter, and more sustainable farming</p>
          </motion.div>
          
          <div className="why-grid">
            {whyChoose.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="why-card"
              >
                <div className="why-icon">
                  <item.icon size={28} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="techiq-cta" id="contact">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-content-techiq"
          >
            <h2>Ready to Transform Your Farm?</h2>
            <p>Join farmers worldwide who are using TechIQ Smart Farming Box to make data-driven decisions</p>
            <div className="cta-buttons-techiq">
              <a href="mailto:sales@voxitconsulting.media" className="btn-techiq-primary">
                Get Started <ArrowRight size={18} />
              </a>
              <a href="mailto:sales@voxitconsulting.media" className="btn-techiq-outline">
                Contact Sales
              </a>
            </div>
            <div className="social-links-techiq">
              <a href="https://www.facebook.com/profile.php?id=61583973157516" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="https://www.linkedin.com/company/techiq-smart-iot-solutions/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <Linkedin size={20} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <PDFDownloadButton 
        contentId="techiqbox-content" 
        fileName="VoxIT-TechIQ-Smart-Farming-Box" 
        buttonText="Download PDF"
      />
    </div>
  );
};

export default TechIQBoxPage;
